import { SOCIAL_PUBLISH_STATUS } from "@/utils/constants/uiConstants";
import {
  fileProcessingStatusToTextAndColor,
  socialPublishStatusToTextAndColor,
} from "@/utils/ui/conversions";
import { THUMBNAIL_SIZE_SUFFIX } from "@/utils";

export type LogCategory =
  | "publish"
  | "metadata"
  | "playlist"
  | "processing"
  | "upload"
  | "reupload"
  | "update";

export type LogStatus = "success" | "error" | "info";

export interface MetaDiff {
  field: string;
  key: string;
  before: string;
  after: string;
  beforeRaw: unknown;
  hasBefore: boolean;
  /**
   * True on a field's first appearance in the logs (no earlier snapshot ever
   * carried it — e.g. a description added for the first time). Rendered as a
   * plain "set to X" line with no before/after columns and no revert.
   */
  establishing?: boolean;
  beforeImage?: string;
  afterImage?: string;
}

export interface DerivedLog {
  id: string;
  mt: number;
  category: LogCategory;
  status: LogStatus;
  title: string;
  description: string;
  details: Record<string, string>;
  metaDiffs: MetaDiff[];
  userId?: string;
}

interface MediaImages {
  base?: string;
  thumb?: string;
  poster?: string;
  portrait?: string;
  social?: string;
  podcast?: string;
  premiere?: string;
  premiere_v?: string;
  [key: string]: unknown;
}

interface SocialEmbed {
  channel_id?: string;
  channel_name?: string;
  job_type?: string;
  status?: number;
  privacy?: string;
  published_id?: string;
  host?: number;
  section?: number;
  playlist?: string[] | string;
  [key: string]: unknown;
}

interface Changes {
  images?: MediaImages;
  socialembeds?: SocialEmbed[];
  state?: number;
  status?: number;
  media_state?: number;
  pub_ts?: number;
  is_reupload?: boolean;
  reason?: string;
  msg?: string;
  playlist?: string;
  mtby?: string;
  mtbyuser?: string;
  ctby?: string;
  [key: string]: unknown;
}

interface RawLog {
  id?: string;
  nid?: number;
  key?: string;
  mt?: number;
  ct?: number;
  ctby?: string;
  changes?: string | Changes;
}

const MAX_DETAIL_LEN = 300;
const STATUS_FAILED_ALT = -2;
const STATE_PROCESSING_DUP = 8;

// Fields never surfaced in the logs UI — mirror-of/derived values users don't
// act on. Excluded from both diffs and the raw detail grid.
const HIDDEN_KEYS = ["stitle", "title_url_edited", "is_title_url_edited"];

const NOISE_KEYS = new Set([
  "id",
  "nid",
  "ct",
  "mt",
  "mtby",
  "mtbyuser",
  "mtuser",
  "ctby",
  "agency_data",
  "is_reupload",
  "reason",
]);

const NON_META_KEYS = new Set<string>([
  ...NOISE_KEYS,
  "state",
  "status",
  "media_state",
  "media_status",
  "pub_ts",
  "socialembeds",
  "socials",
  "published_id",
  "playlist",
  "playlists",
  "images",
  ...HIDDEN_KEYS,
]);

const UPLOAD_STATES = new Set([1, 2, 3, 4, -2, -3, -4]);
const LIFECYCLE_STATES = new Set([1, 2, 3, 4, 6, 7, 9, 10, -2, -3, -4, -6, -7]);

const IMAGE_FIELDS: ReadonlyArray<{ key: keyof MediaImages; label: string }> = [
  { key: "thumb", label: "Landscape Thumbnail" },
  { key: "poster", label: "Poster" },
  { key: "portrait", label: "Portrait" },
  { key: "social", label: "Social" },
  { key: "podcast", label: "Podcast" },
  { key: "premiere", label: "Premiere" },
  { key: "premiere_v", label: "Premiere (Portrait)" },
];

const META_LABEL_OVERRIDES: Record<string, string> = {
  desc: "Description",
  ctype: "Content Type",
  agencies: "Agency",
  agency_credits: "Agency Credits",
  meta_lang: "Meta Language",
  lang: "Language",
  videocredit: "Video Credit",
  audiocredit: "Audio Credit",
  imagecredit: "Image Credit",
  campaign_id: "Campaign",
  category_tags: "Category Tags",
  denmark_keywords: "Denmark Keywords",
};

const LIFECYCLE_CATEGORIES: ReadonlySet<LogCategory> = new Set<LogCategory>([
  "upload",
  "reupload",
  "processing",
  "publish",
  "playlist",
]);

const isEmpty = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && v !== null && Object.keys(v).length === 0);

const has = (o: Record<string, unknown>, k: string): boolean =>
  !isEmpty(o?.[k]);

const pluralized = (count: number): string => (count === 1 ? "" : "s");

export const parseChanges = (changes: RawLog["changes"]): Changes | null => {
  if (!changes) return null;
  try {
    return typeof changes === "string" ? JSON.parse(changes) : changes;
  } catch {
    return null;
  }
};

/**
 * id → display name, populated per `deriveLogs` call. Lets `stringify` render
 * user-id field values (e.g. contributors like script_writer) as names.
 */
let nameResolver: (id: string) => string = (id) => id;

const stringify = (v: unknown): string => {
  if (isEmpty(v)) return "(empty)";
  if (Array.isArray(v)) {
    return v
      .map((x) =>
        typeof x === "object" && x !== null
          ? ((x as Record<string, unknown>).name ??
            (x as Record<string, unknown>).channel_name ??
            (x as Record<string, unknown>).title ??
            JSON.stringify(x))
          : nameResolver(String(x))
      )
      .join(", ");
  }
  if (typeof v === "object" && v !== null) {
    return Object.entries(v as Record<string, unknown>)
      .filter(([, vv]) => !isEmpty(vv))
      .map(([kk, vv]) => `${kk}: ${vv}`)
      .join(" · ");
  }
  return String(v);
};
const labelForKey = (key: string): string =>
  META_LABEL_OVERRIDES[key] ??
  key.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());

const changedMetaKeys = (c: Changes): string[] =>
  Object.keys(c).filter((k) => !NON_META_KEYS.has(k) && !isEmpty(c[k]));

const isThumbnailChange = (images: unknown): images is MediaImages =>
  !!images &&
  typeof images === "object" &&
  !!(images as MediaImages).base &&
  IMAGE_FIELDS.some(({ key }) => !!(images as MediaImages)[key]);
const imageUrl = (base?: string, id?: string): string | undefined => {
  if (!base || !id) return undefined;
  return `${base.replace(/\/$/, "")}/${id}${THUMBNAIL_SIZE_SUFFIX}.jpg`;
};

type PublishPhase = "published" | "failed" | "draft" | "pending";

const embedPhase = (embed: SocialEmbed): PublishPhase => {
  const s = embed?.status;
  if (s === undefined || s === null) return "pending";
  if (s === SOCIAL_PUBLISH_STATUS.COMPLETED) return "published";
  if (s === SOCIAL_PUBLISH_STATUS.FAILED || s === STATUS_FAILED_ALT)
    return "failed";
  if (s === SOCIAL_PUBLISH_STATUS.QUEUE || s === SOCIAL_PUBLISH_STATUS.DRAFT)
    return "draft";
  return "pending";
};

const embedLogStatus = (embed: SocialEmbed): LogStatus => {
  switch (embedPhase(embed)) {
    case "failed":
      return "error";
    case "published":
      return "success";
    default:
      return "info";
  }
};

const platformLabel = (jobType?: string): string =>
  jobType ? jobType[0].toUpperCase() + jobType.slice(1) : "channel";

const embedTitle = (embed: SocialEmbed): string => {
  const platform = platformLabel(embed?.job_type);
  switch (embedPhase(embed)) {
    case "published":
      return `Published to ${platform}`;
    case "failed":
      return `Publish to ${platform} failed`;
    case "draft":
      return `Draft — ${platform} publish`;
    default:
      return `Publishing to ${platform}`;
  }
};

const embedDetails = (embed: SocialEmbed): Record<string, string> => {
  const out: Record<string, string> = {};
  const put = (label: string, v: unknown) => {
    if (!isEmpty(v)) out[label] = stringify(v);
  };
  put("Channel", embed?.channel_name);
  put("Platform", embed?.job_type);
  if (embed?.status !== undefined && embed?.status !== null) {
    out["Status"] = socialPublishStatusToTextAndColor(embed.status).statusText;
  }
  put("Privacy", embed?.privacy);
  put("Published id", embed?.published_id);
  put("Channel id", embed?.channel_id);
  put("Host", embed?.host);
  put("Section", embed?.section);
  put("Playlist", embed?.playlist);
  return out;
};

const embedDedupKey = (embed: SocialEmbed): string =>
  `${embed?.channel_id ?? embed?.channel_name}|${embed?.job_type}|${embedPhase(
    embed
  )}`;

const deriveCategory = (c: Changes): LogCategory => {
  if (c?.is_reupload === true) return "reupload";
  if (has(c, "socialembeds") || has(c, "socials") || has(c, "published_id")) {
    return "publish";
  }
  if (has(c, "playlist")) return "playlist";
  if (has(c, "state") && LIFECYCLE_STATES.has(Number(c?.state))) {
    return UPLOAD_STATES.has(Number(c?.state)) ? "upload" : "processing";
  }
  if (changedMetaKeys(c).length > 0 || isThumbnailChange(c?.images)) {
    return "metadata";
  }
  if (has(c, "media_state") || has(c, "media_status") || has(c, "pub_ts")) {
    return "processing";
  }
  if (has(c, "source") || has(c, "fid")) return "upload";
  return "update";
};

const deriveStatus = (c: Changes): LogStatus => {
  const embeds = c?.socialembeds;
  if (Array.isArray(embeds) && embeds.length > 0) {
    if (embeds.some((e) => embedLogStatus(e) === "error")) return "error";
    if (embeds.every((e) => e?.status === SOCIAL_PUBLISH_STATUS.COMPLETED))
      return "success";
    return "info";
  }
  if (has(c, "error") || has(c, "msg")) return "error";
  if (has(c, "state") && Number(c?.state) < 0) return "error";
  return "success";
};

const deriveTitle = (c: Changes, category: LogCategory): string => {
  switch (category) {
    case "publish":
      return embedTitle((c.socialembeds?.[0] as SocialEmbed) ?? {});
    case "playlist":
      return "Added to playlist";
    case "metadata": {
      const changed = changedMetaKeys(c);
      const thumb = isThumbnailChange(c?.images);
      if (changed.length === 0 && thumb) return "Thumbnail updated";
      if (changed.length === 1 && !thumb)
        return `${labelForKey(changed[0])} updated`;
      return "Metadata updated";
    }
    case "processing": {
      const st = Number(c?.state);
      if (st === 6 || st === 7) return "Processing started";
      if (st === 9) return "Verifying";
      if (st === 10) return "Processing completed";
      if (st < 0) return "Processing failed";
      if (Number(c?.media_state) === 1 || has(c, "pub_ts"))
        return "Media published";
      return "Media state updated";
    }
    case "upload": {
      const st = Number(c?.state);
      if (st === 1) return "Upload started";
      if (st === 2) return "Uploading";
      if (st === 3 || st === 4) return "Upload completed";
      if (st < 0) return "Upload failed";
      return "Media uploaded";
    }
    case "reupload":
      return "Media re-uploaded";
    default:
      return "Media updated";
  }
};

const changedFieldsSummary = (c: Changes, includeThumb: boolean): string => {
  const labels = changedMetaKeys(c).map(labelForKey);
  if (includeThumb && isThumbnailChange(c?.images)) labels.push("Thumbnail");
  if (!labels.length) return "";
  return `${labels.length} field${pluralized(labels.length)} changed: ${labels.join(", ")}`;
};

const deriveDescription = (c: Changes, category: LogCategory): string => {
  switch (category) {
    case "publish": {
      const channel = c.socialembeds?.[0]?.channel_name;
      return channel ? `Channel: ${channel}` : "Distribution channel updated";
    }
    case "playlist":
      return `Playlist: ${c.playlist}`;
    case "metadata":
      return changedFieldsSummary(c, true) || "Metadata changed";
    case "upload":
      return fileProcessingStatusToTextAndColor(Number(c?.state)).statusText;
    case "processing":
      return has(c, "pub_ts")
        ? "Media marked live"
        : fileProcessingStatusToTextAndColor(Number(c?.state)).statusText;
    case "reupload":
      return (
        c?.reason ||
        c?.msg ||
        "Editor replaced the original upload with a new version."
      );
    default:
      return changedFieldsSummary(c, false) || "Media details updated";
  }
};
const deriveDetails = (c: Changes): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(c)) {
    if (NOISE_KEYS.has(k) || HIDDEN_KEYS.includes(k) || isEmpty(v)) continue;
    const display = stringify(v);
    out[k] =
      display.length > MAX_DETAIL_LEN
        ? `${display.slice(0, MAX_DETAIL_LEN)}…`
        : display;
  }
  return out;
};

const buildImageDiffs = (
  c: Changes,
  lastValue: Record<string, MediaImages>
): MetaDiff[] => {
  if (!isThumbnailChange(c.images)) return [];
  const imgs = c.images;
  const prevImgs = lastValue.images ?? {};
  const base = imgs.base ?? prevImgs.base;
  const diffs: MetaDiff[] = [];

  for (const { key, label } of IMAGE_FIELDS) {
    const afterId = imgs[key] as string | undefined;
    if (!afterId) continue;
    const beforeId = prevImgs[key] as string | undefined;
    if (beforeId && beforeId === afterId) continue;
    diffs.push({
      field: label,
      key: `images.${key}`,
      before: "",
      after: "",
      beforeRaw: prevImgs,
      hasBefore: !!beforeId,
      establishing: !beforeId,
      beforeImage: imageUrl(prevImgs.base ?? base, beforeId),
      afterImage: imageUrl(base, afterId),
    });
  }
  lastValue.images = { ...prevImgs, ...imgs };
  return diffs;
};

const buildFieldDiffs = (
  c: Changes,
  lastValue: Record<string, unknown>
): MetaDiff[] => {
  const diffs: MetaDiff[] = [];
  for (const key of changedMetaKeys(c)) {
    const prev = lastValue[key];
    // First appearance = no earlier snapshot (incl. the upload/creation one)
    // carried this key. Present-at-creation fields are seeded by that snapshot,
    // so their later edits have a real `prev` and diff normally.
    const firstAppearance = !(key in lastValue);
    const beforeStr = stringify(prev);
    const afterStr = stringify(c[key]);
    if (!firstAppearance && !isEmpty(prev) && beforeStr === afterStr) continue;
    diffs.push({
      field: labelForKey(key),
      key,
      before: beforeStr,
      after: afterStr,
      beforeRaw: prev,
      hasBefore: !firstAppearance && !isEmpty(prev),
      establishing: firstAppearance,
    });
    lastValue[key] = c[key];
  }
  return diffs;
};

const makeEntry = (
  raw: RawLog,
  c: Changes,
  overrides: Partial<DerivedLog> & Pick<DerivedLog, "id" | "category">
): DerivedLog => {
  const category = overrides.category;
  return {
    mt: Number(raw?.mt ?? c?.mt ?? 0),
    status: deriveStatus(c),
    title: deriveTitle(c, category),
    description: deriveDescription(c, category),
    details: {},
    metaDiffs: [],
    userId: (c?.mtby ?? c?.mtbyuser ?? c?.ctby ?? raw?.ctby) as
      | string
      | undefined,
    ...overrides,
  };
};

const isDuplicateProcessing = (c: Changes): boolean =>
  Number(c?.state) === STATE_PROCESSING_DUP &&
  !has(c, "title") &&
  !has(c, "socialembeds");

export const deriveLogs = (
  items: RawLog[] | undefined,
  mediaKey?: string,
  systemMode = false,
  resolveName?: (id: string) => string
): DerivedLog[] => {
  nameResolver = resolveName ?? ((id) => id);
  const chronological = (items ?? [])
    .filter((it) => !mediaKey || it?.key === mediaKey)
    .slice()
    .sort((a, b) => Number(a?.mt ?? 0) - Number(b?.mt ?? 0));

  const lastValue: Record<string, unknown> = {};
  const seenEmbeds = new Set<string>();
  const derived: DerivedLog[] = [];
  let prevSignature: string | null = null;

  for (const raw of chronological) {
    const c = parseChanges(raw?.changes);
    if (!c) continue;
    if (!systemMode) {
      if (isDuplicateProcessing(c)) continue;
      const { ct: _ct, mt: _mt, id: _id, ...meaningful } = c;
      const signature = JSON.stringify(meaningful);
      if (signature === prevSignature) continue;
      prevSignature = signature;
    }

    const category = deriveCategory(c);
    const baseId = String(raw?.id ?? c?.id ?? raw?.nid);

    const metaDiffs = [
      ...buildImageDiffs(c, lastValue as Record<string, MediaImages>),
      ...buildFieldDiffs(c, lastValue),
    ];
    if (category === "publish" && Array.isArray(c.socialembeds)) {
      c.socialembeds.forEach((embed, i) => {
        if (!systemMode) {
          const key = embedDedupKey(embed);
          if (seenEmbeds.has(key)) return;
          seenEmbeds.add(key);
        }
        derived.push(
          makeEntry(raw, c, {
            id: `${baseId}-pub-${i}`,
            category: "publish",
            status: embedLogStatus(embed),
            title: embedTitle(embed),
            description: embed?.channel_name
              ? `Channel: ${embed.channel_name}`
              : "Distribution channel updated",
            details: embedDetails(embed),
          })
        );
      });
      continue;
    }
    if (LIFECYCLE_CATEGORIES.has(category)) {
      derived.push(
        makeEntry(raw, c, {
          id: `${baseId}-evt`,
          category,
          details: deriveDetails(c),
        })
      );
      continue;
    }
    if (metaDiffs.length > 0) {
      const labels = metaDiffs.map((d) => d.field);
      derived.push(
        makeEntry(raw, c, {
          id: `${baseId}-meta`,
          category: "metadata",
          status: "success",
          title:
            labels.length === 1 ? `${labels[0]} updated` : "Metadata updated",
          description: `${labels.length} field${pluralized(
            labels.length
          )} changed: ${labels.join(", ")}`,
          metaDiffs,
        })
      );
      continue;
    }
    const details = deriveDetails(c);
    if (!systemMode && Object.keys(details).length === 0) continue;
    derived.push(
      makeEntry(raw, c, { id: baseId, category, details, metaDiffs })
    );
  }
  return derived.reverse();
};
